/* ==========================================================================
   Wander.africa — theme behaviour
   ==========================================================================
   Divergences from Source's main.js, all deliberate:

   · Mobile menu is rewritten as an accessible disclosure: it manages the
     `hidden` attribute and `aria-expanded`, closes on Escape, and returns focus
     to the button. Source only toggled a class.
   · The overflow "dropdown" nav is dropped. Wander's navigation is deliberately
     short (brief §24 — no mega-menu), so collapsing items into a submenu solved
     a problem we don't have and shipped 2KB of layout-thrashing measurement.
   · Infinite-scroll pagination is dropped. Real, crawlable pagination links are
     worth more than the interaction novelty on a search-acquisition site, and
     infinite scroll makes "page 2 exists" invisible to Google.
   · Reading progress is added for long planning articles (opt-in per setting).
   ========================================================================== */


/* Reader theme. First visits stay light; only an explicit click persists dark. */
(function () {
    const button = document.querySelector('[data-theme-toggle]');
    if (!button) return;

    const root = document.documentElement;
    const themeMeta = document.querySelector('#theme-color');

    const applyTheme = function (theme, persist) {
        const isDark = theme === 'dark';
        root.dataset.theme = isDark ? 'dark' : 'light';
        button.setAttribute('aria-pressed', isDark ? 'true' : 'false');
        button.setAttribute('aria-label', isDark ? 'Use light theme' : 'Use dark theme');
        button.setAttribute('title', isDark ? 'Use light theme' : 'Use dark theme');
        if (themeMeta) themeMeta.setAttribute('content', isDark ? '#171716' : '#fcfbf8');

        if (persist) {
            try {
                localStorage.setItem('wander-theme', isDark ? 'dark' : 'light');
            } catch (error) {
                /* The control still works for this page when storage is blocked. */
            }
        }
    };

    applyTheme(root.dataset.theme, false);
    button.addEventListener('click', function () {
        applyTheme(root.dataset.theme === 'dark' ? 'light' : 'dark', true);
    });
})();
/* Responsive sections menu, implemented as an accessible modal disclosure.
   Mobile uses a full-viewport sheet; desktop places the same content in a
   left-side drawer over a backdrop. Both variants lock page scroll, keep focus
   inside while open, close on Escape, and return focus to the trigger. */
(function () {
    const navigation = document.querySelector('.gh-navigation');
    if (!navigation) return;

    const burger = navigation.querySelector('.gh-burger');
    /* The modal is a sibling of the header, not a child: the header's backdrop
       filter would otherwise contain its fixed positioning. */
    const menu = document.querySelector('#gh-mobile-menu');
    if (!burger || !menu) return;

    const closeButton = menu.querySelector('.gh-menu-close');
    const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
    let lastFocus = null;

    const setOpen = function (open) {
        navigation.classList.toggle('is-open', open);
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');

        if (open) {
            lastFocus = document.activeElement;
            menu.removeAttribute('hidden');
            menu.classList.remove('hidden');
            /* Compensate for the scrollbar so the page does not shift. */
            const gutter = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.overflow = 'hidden';
            if (gutter > 0) document.documentElement.style.paddingRight = `${gutter}px`;
            (closeButton || menu.querySelector(FOCUSABLE))?.focus();
        } else {
            menu.setAttribute('hidden', '');
            menu.classList.add('hidden');
            document.documentElement.style.overflow = '';
            document.documentElement.style.paddingRight = '';
            (lastFocus instanceof HTMLElement ? lastFocus : burger).focus();
        }
    };

    const isOpen = () => burger.getAttribute('aria-expanded') === 'true';

    burger.addEventListener('click', () => setOpen(!isOpen()));
    closeButton?.addEventListener('click', () => setOpen(false));

    document.addEventListener('keydown', function (event) {
        if (!isOpen()) return;

        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }

        /* Focus trap: Tab cycles within the visible sheet or drawer. */
        if (event.key === 'Tab') {
            const items = [...menu.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    });

    /* Links close both variants. On desktop, clicking the dimmed outer element
       also dismisses the drawer; on mobile the panel fills that element. */
    menu.addEventListener('click', function (event) {
        if (event.target.closest('a') || event.target === menu) setOpen(false);
    });
})();

/* Reading progress — decorative, hidden from assistive tech in the template */
(function () {
    const bar = document.querySelector('.gh-progress-bar');
    const article = document.querySelector('.gh-content');
    if (!bar || !article) return;

    let frame = null;
    const update = function () {
        const start = article.offsetTop;
        const total = article.offsetHeight - window.innerHeight * 0.4;
        const progress = (window.scrollY - start) / (total > 0 ? total : 1);
        bar.style.width = Math.min(Math.max(progress, 0), 1) * 100 + '%';
        frame = null;
    };

    window.addEventListener('scroll', function () {
        if (frame === null) frame = window.requestAnimationFrame(update);
    }, {passive: true});
    update();
})();

/* Gallery lightbox for Koenig image and gallery cards */
(function () {
    lightbox(
        '.kg-image-card > .kg-image[width][height], .kg-gallery-image > img'
    );
})();

/* Responsive video embeds inside editor content */
(function () {
    const sources = [
        '.gh-content iframe[src*="youtube.com"]',
        '.gh-content iframe[src*="youtube-nocookie.com"]',
        '.gh-content iframe[src*="player.vimeo.com"]',
        '.gh-content object',
        '.gh-content embed'
    ];
    reframe(document.querySelectorAll(sources.join(',')));
})();

/* Wrap editor tables so wide travel-data tables scroll instead of overflowing */
(function () {
    const tables = document.querySelectorAll('.gh-content > table:not(.gist table)');
    tables.forEach(function (table) {
        const wrapper = document.createElement('div');
        wrapper.className = 'gh-table overflow-x-auto';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
    });
})();

/* Affiliate + outbound click tracking.
   Fires a GA4 event when a reader follows a commercial link, so we can measure
   which component types actually convert (docs/research/keywords.md §8).
   No-ops silently when no analytics tag is present. */
(function () {
    document.addEventListener('click', function (event) {
        const link = event.target.closest('a[data-affiliate]');
        if (!link || typeof window.gtag !== 'function') return;
        window.gtag('event', 'affiliate_click', {
            partner: link.getAttribute('data-affiliate'),
            component: link.getAttribute('data-component') || 'unknown',
            destination: link.getAttribute('data-destination') || 'unknown',
            link_url: link.href
        });
    }, {passive: true});
})();
